import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import SearchOverlay from "./SearchOverlay";
import { useGiftCardHandle } from "@/lib/giftCard";

const LEFT_LINKS = [
  { label: "Shop", href: "/shop/men" },
  { label: "Collection", href: "/collection" },
  { label: "About", href: "/about" },
];

export default function Navbar() {
  const { user } = useAuth();
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const giftCard = useGiftCardHandle();
  const links = giftCard ? [...LEFT_LINKS, { label: "Gift Card", href: `/products/${giftCard}` }] : LEFT_LINKS;

  const linkClass = (href) =>
    `font-['Oswald'] text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
      location.pathname === href ? "text-[#F2F0EB]" : "text-[#8A8A8A] hover:text-[#F2F0EB]"
    }`;

  return (
    <>
      <nav
        data-testid="main-navbar"
        className="sticky top-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-[#8A8A8A]/20"
      >
        <div className="grid grid-cols-3 items-center px-5 sm:px-10 py-5">
          {/* Left — desktop links / mobile hamburger */}
          <div className="flex items-center gap-8 justify-start">
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="hidden md:flex items-center gap-8">
              {links.map((link) => (
                <Link key={link.href} to={link.href} data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkClass(link.href)}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Center — logo */}
          <div className="flex justify-center">
            <Link to="/" data-testid="navbar-logo" className="font-['Oswald'] text-xl sm:text-2xl font-bold uppercase tracking-[0.3em] text-[#F2F0EB]">
              AERWEAR
            </Link>
          </div>

          {/* Right — actions */}
          <div className="flex items-center gap-5 sm:gap-6 justify-end">
            <button data-testid="nav-link-search" onClick={() => setSearchOpen(true)} className="p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors" aria-label="Search">
              <Search size={18} />
            </button>
            <Link
              to={user && user !== false ? "/account" : "/login"}
              data-testid="nav-link-account"
              className="p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors hidden sm:block"
              aria-label="Account"
            >
              <User size={18} />
            </Link>
            <button data-testid="nav-link-cart" onClick={() => setIsOpen(true)} className="relative p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors" aria-label="Cart">
              <ShoppingBag size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#F2F0EB] text-[#0A0A0A] text-[9px] font-bold w-4 h-4 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div data-testid="mobile-menu" className="md:hidden border-t border-[#8A8A8A]/20 bg-[#0A0A0A]">
            {links.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-6 py-5 font-['Oswald'] text-sm uppercase tracking-[0.25em] text-[#8A8A8A] hover:text-[#F2F0EB] border-b border-[#8A8A8A]/10 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to={user && user !== false ? "/account" : "/login"}
              data-testid="mobile-nav-link-account"
              className="block px-6 py-5 font-['Oswald'] text-sm uppercase tracking-[0.25em] text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Account
            </Link>
          </div>
        )}
      </nav>

      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
